import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import find from 'lodash/find';
import includes from 'lodash/includes';

import { BrowseBucketContainer } from '../../containers';
import { BrandBucket } from '../BrandBucket';
import {
    CATEGORY_TEMPLATE,
    NEARBY_CITIES_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    COLLECTION_TEMPLATE,
    SHARED_BUCKET_SPACING_CLASSES,
    NON_EVENT_BUCKET_SPACING_CLASSES,
    BUCKET_WRAPPER_WITH_DECORATION_CLASSES,
    DECORATIVE_IMAGE_PLACEMENT_MAP,
    EDITORIAL_ARTICLES_BUCKET_KEY,
    ORGANIZER_FOLLOW_TEMPLATE,
} from '../../../../constants';

import './mainContent.scss';

const NON_EVENT_BUCKETS = [
    CATEGORY_TEMPLATE,
    NEARBY_CITIES_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    ORGANIZER_FOLLOW_TEMPLATE,
];

const SMALL_CITY_KEY = 'small_city_fallback';

/*
 Returns the decorativeImagePlacement matching the selector and adds it to an array to prevent duplicates
*/
const _getDecorativeImagePlacement = (key, usedDecorativeImages) => {
    const decorativeImage = find(
        DECORATIVE_IMAGE_PLACEMENT_MAP,
        (decorativeImageData) => {
            const { selectors } = decorativeImageData;

            let isKeyMappedInPlacementMap;

            if (!includes(usedDecorativeImages, selectors)) {
                isKeyMappedInPlacementMap = includes(selectors, key);

                if (isKeyMappedInPlacementMap) {
                    usedDecorativeImages.push(selectors);
                }
            }

            return isKeyMappedInPlacementMap;
        },
    );

    return decorativeImage;
};

const getDecorativeImage = (decorativeImages, key, usedImages) => {
    let decorativeImage;

    const decorationPlacement = _getDecorativeImagePlacement(key, usedImages);

    if (decorationPlacement) {
        decorativeImage = decorativeImages[decorationPlacement.urlSelector];
    }

    return { decorationPlacement, decorativeImage };
};

const getBucketClasses = (key, decorativeImage, template) => {
    // Only the articles and collections section should have a gray background
    const shouldHaveBackground =
        key === EDITORIAL_ARTICLES_BUCKET_KEY || key === COLLECTION_TEMPLATE;

    const containerClasses = classNames({
        'eds-bg-color--grey-100': shouldHaveBackground,
    });

    let spacingClasses = SHARED_BUCKET_SPACING_CLASSES;

    if (includes(NON_EVENT_BUCKETS, template)) {
        spacingClasses = NON_EVENT_BUCKET_SPACING_CLASSES;
    }

    const bucketWrapperClasses = classNames(spacingClasses, {
        [BUCKET_WRAPPER_WITH_DECORATION_CLASSES]: decorativeImage,
        'collection-bucket-top-spacing': key === COLLECTION_TEMPLATE,
    });

    return { containerClasses, bucketWrapperClasses };
};

const _getBuckets = (bucketConfigs, decorativeImages) => {
    /* Creates an array to keep the used decorativeImages and prevent duplicates. */
    const usedDecorativeImages = [];

    return bucketConfigs.map((bucketConfig = {}, index) => {
        const { template = '', key = '', name = '' } = bucketConfig;

        /* DECORITIVE IMAGE LOGIC */
        const { decorativeImage, decorationPlacement } = getDecorativeImage(
            decorativeImages,
            key,
            usedDecorativeImages,
        );
        /* END DECORATIVE IMAGE LOGIC */

        /* CONTAINER CLASS LOGIC */
        const { bucketWrapperClasses, containerClasses } = getBucketClasses(
            key,
            decorativeImage,
            template,
        );
        /* END CONTAINER CLASS LOGIC */

        return (
            <div key={name} className={`${key}--bucket-wrapper`}>
                <BrowseBucketContainer
                    bucketConfig={bucketConfig}
                    containerClasses={containerClasses}
                    bucketWrapperClasses={bucketWrapperClasses}
                    decorationPlacement={decorationPlacement}
                    decorativeImage={decorativeImage}
                    bucketIndex={index}
                />
            </div>
        );
    });
};

const _insertBrandBucket = (bucketSections, middleImage, middleSmallImage) => {
    if (!middleImage) {
        return bucketSections;
    }

    //We want to insert the large image midway down the page. But it shouldn't
    //be immediately next to one of our decorative images
    const midIndex = Math.floor(bucketSections.length / 2);
    const insertIndex = includes(
        Object.keys(DECORATIVE_IMAGE_PLACEMENT_MAP),
        `${midIndex}`,
    )
        ? midIndex + 1
        : midIndex;

    const middleImageComponent = (
        <div key="brand-image">
            <BrandBucket
                middleImage={middleImage}
                middleSmallImage={middleSmallImage}
            />
        </div>
    );

    return [
        ...bucketSections.slice(0, insertIndex),
        middleImageComponent,
        ...bucketSections.slice(insertIndex, bucketSections.length),
    ];
};

export class MainContent extends React.PureComponent {
    static propTypes = {
        /*
         * Array of formatted buckets
         */
        bucketConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
        /*
         * Url for middle brand image
         */
        middleImage: PropTypes.string,
        /*
         * Url for middle brand image on small viewports
         */
        middleSmallImage: PropTypes.string,
        /*
         * Url for artist supplied decorative image
         */
        decorationTopLeftUrl: PropTypes.string,
        /*
         * Url for artist supplied decorative image
         */
        decorationTopRightUrl: PropTypes.string,
        /*
         * Url for artist supplied decorative image
         */
        decorationBottomLeftUrl: PropTypes.string,
        /*
         * Url for artist supplied decorative image
         */
        decorationBottomRightUrl: PropTypes.string,
    };

    render() {
        let { bucketConfigs } = this.props;
        const {
            middleImage,
            middleSmallImage,
            decorationTopLeftUrl,
            decorationTopRightUrl,
            decorationBottomLeftUrl,
            decorationBottomRightUrl,
        } = this.props;

        const decorativeImages = {
            decorationTopLeftUrl,
            decorationTopRightUrl,
            decorationBottomLeftUrl,
            decorationBottomRightUrl,
        };

        /* Insert Follow Organizer Bucket into the response. Once
         backend does this for us will no longer be necessary */
        const [
            firstBucket,
            secondBucket,
            ...restOfBucketConfigs
        ] = bucketConfigs;
        const isLargeCity = firstBucket && firstBucket.key !== SMALL_CITY_KEY;

        const organizerFollowBucket = {
            key: 'follow_organizer',
            name: 'Featured Organizers',
            template: ORGANIZER_FOLLOW_TEMPLATE,
            type: 'follow_organizer',
        };

        if (isLargeCity) {
            bucketConfigs = [
                firstBucket,
                secondBucket,
                organizerFollowBucket,
                ...restOfBucketConfigs,
            ];
        } else {
            bucketConfigs = [
                firstBucket,
                organizerFollowBucket,
                secondBucket,
                ...restOfBucketConfigs,
            ];
        }

        const bucketSections = _getBuckets(bucketConfigs, decorativeImages);

        const bucketSectionsWithBrand = _insertBrandBucket(
            bucketSections,
            middleImage,
            middleSmallImage,
        );

        return <div className="main-content">{bucketSectionsWithBrand}</div>;
    }
}
